import React, { Children, useEffect, useState, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { renderToString } from "react-dom/server";
import BounceLoader from "react-spinners/BounceLoader";
import GoogleMapReact, { ChangeEventValue } from "google-map-react";
import cn from "classnames";

import { formatPriceDisplay } from "capitalrx-utilities";

import Button from "../../components/Button";
import styles from "./GoogleMap.module.scss";
import { CustomInfoWindow } from "./CustomInfoWindow";
import { PharmacyMapList, InfoWindow, LatLngBounds, Marker, Map, Maps, MapProps } from "./GoogleMap.props";
import { defaultBoundingBox } from "./mockData";

export const GoogleMap: React.FC<MapProps> = (props: MapProps) => {
  const {
    allowRedoSearch = true,
    center,
    handleInfoWindowButtonClick,
    defaultZoom = 14,
    handleRedoSearch = () => undefined,
    isLoading = true,
    mapView = false,
    pharmacies = [],
    searchButtonPosition = "right",
    showInfoWindowButton = true,
    showSearchButton = true,
    toggleViewButton,
  } = props;

  const [mapObj, setMapObj] = useState<Map>();
  const [mapsObj, setMapsObj] = useState<Maps>();
  const [markers, setMarkers] = useState<Array<Marker | null>>([]);
  const [maxMapZoom, setMaxMapZoom] = useState(18);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [lastOpenedInfoWindow, setLastOpenedInfoWindow] = useState<InfoWindow>();
  const [centerValue, setCenterValue] = useState<MapProps["center"]>(center);
  const [boundingBoxValues, setBoundingBoxValues] = useState(defaultBoundingBox);
  const lastOpenedRef = React.useRef(lastOpenedInfoWindow);

  const mapStyles = cn(styles.PharmacyMapContainer, "");
  const RedoSearchOverlayStyles = cn(styles.RedoSearchOverlay, styles[searchButtonPosition], "redo-search-button");

  // clear and update the map markets if list of pharmacies change
  useEffect(() => {
    if (markers) {
      const newMarkers = markers.map((marker) => {
        marker && marker.setMap(null);

        return marker;
      });
      setMarkers(newMarkers);
    }

    if (pharmacies.length) {
      setCenterValue(center);
      updateBounds();
    }

    if (pharmacies.length === 1) {
      setMaxMapZoom(18);
    }

    setMapZoom(14);
  }, [JSON.stringify(pharmacies), markers.length, mapView]);

  const setLastOpened = (data: InfoWindow): void => {
    lastOpenedRef.current = data;
    setLastOpenedInfoWindow(data);
  };

  const closeInfoWindow = (map: Map, marker: Marker, infoWindow: InfoWindow): void => {
    if (lastOpenedRef.current) {
      const lastOpened: InfoWindow = lastOpenedRef.current;
      lastOpened.close();
    }
    infoWindow.open(map, marker);
  };

  const addMarkers = (map: Map, maps: Maps): void => {
    if (pharmacies && maps) {
      const mapMarkers = pharmacies.map(({ pharmacy, distance, price }: PharmacyMapList, idx: number): Marker => {
        const id = `pharmacy_${idx}`;
        const infoWindow = new maps.InfoWindow({
          content: renderToString(<CustomInfoWindow id={id} distance={distance} pharmacy={pharmacy} />),
        });

        if (handleInfoWindowButtonClick && showInfoWindowButton) {
          maps.event.addListener(infoWindow, "domready", () => {
            const pricingDisplay = isNaN(Number(price)) ? "Check at Pharmacy" : formatPriceDisplay(Number(price));

            const button = (
              <Button
                testId="redo-search-button"
                disabled={isLoading}
                ariaLabel="View Pharmacy Price"
                size="xsmall"
                fullWidth
                icon="carat-right"
                iconLocation="right"
                onClick={() => handleInfoWindowButtonClick(idx)}
                className="redo-search-button"
              >
                <BounceLoader cssOverride={override} loading={isLoading} size={24} />
                <span>{pricingDisplay}</span>
              </Button>
            );

            const rootElement = document.getElementById(id) as HTMLElement;
            const root = createRoot(rootElement);
            root.render(Children.only(button));
          });
        }

        const marker = new maps.Marker({
          map: map,
          position: { lat: Number(pharmacy.latitude), lng: Number(pharmacy.longitude) },
          Animation: maps.Animation.Drop,
          title: pharmacy.name,
        });

        maps.event.addListener(map, "click", () => {
          infoWindow.close();
        });

        marker.addListener("click", () => closeInfoWindow(map, marker, infoWindow));
        marker.addListener("click", () => setLastOpened(infoWindow));

        return marker;
      });

      setMarkers(mapMarkers);
    }
  };

  const getMapBounds = (maps: Maps, pharmacies: Array<PharmacyMapList>): LatLngBounds => {
    const latLngBounds = new maps.LatLngBounds();
    pharmacies.forEach(({ pharmacy }) => latLngBounds.extend(new maps.LatLng(pharmacy.latitude, pharmacy.longitude)));
    return latLngBounds;
  };

  // add the markers and update the bounds to fit them
  const updateBounds = (): void => {
    if (mapObj && mapsObj && pharmacies.length > 0) {
      addMarkers(mapObj, mapsObj);
      const bounds = getMapBounds(mapsObj, pharmacies);

      // fit map to bounds
      mapObj.fitBounds(bounds);
    }
  };

  const onChange = useCallback(
    (e: ChangeEventValue): void => {
      setBoundingBoxValues(e.bounds);
      setCenterValue(e.center);
    },
    [pharmacies, centerValue],
  );

  // fit map to its bounds after the api is loaded
  const apiIsLoaded = useCallback(
    (map: Map, maps: Maps, _pharmacies: Array<PharmacyMapList>): void => {
      setMapObj(map);
      setMapsObj(maps);
      addMarkers(map, maps);
    },
    [pharmacies],
  );

  const override = { marginRight: "10px" };

  const MapButton = () => {
    if (showSearchButton) {
      return (
        <span className={styles.MapOverlayButtons}>
          {toggleViewButton}
          <Button
            testId="redo-search-button"
            className={RedoSearchOverlayStyles}
            disabled={isLoading}
            ariaLabel="Redo search on map button"
            size="small"
            onClick={() => {
              handleRedoSearch({
                lat: centerValue.lat,
                lng: centerValue.lng,
                bounding_box: { ...boundingBoxValues },
              });
            }}
          >
            <BounceLoader cssOverride={override} loading={isLoading} size={24} />
            <span>REDO SEARCH ON MAP</span>
          </Button>
        </span>
      );
    } else {
      return null;
    }
  };

  return (
    <div className={mapStyles} role="presentation">
      {isLoading && <div className={styles.MapOverlay} />}
      <GoogleMapReact
        bootstrapURLKeys={{ key: String(process.env.REACT_APP_TEMP_GOOGLE_MAP_API_KEY) }}
        center={{ lat: centerValue.lat, lng: centerValue.lng }}
        zoom={mapZoom}
        onChange={(e): void => onChange(e)}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }): void => {
          apiIsLoaded(map, maps, pharmacies);
        }}
        options={{ fullscreenControl: false, maxZoom: maxMapZoom }}
      />

      {allowRedoSearch ? <MapButton /> : null}
    </div>
  );
};
